import { useState } from "react"
import styled from "styled-components"

import {
  Banner,
  Checkbox,
  RadioButton,
  spacing,
  Text,
} from "@minuthq/meatball-ui-react"
import { Badge } from "@minuthq/meatball-ui-react"
import { differenceInDays } from "date-fns"

import { ParadiseDetailsSection } from "src/components/Paradise/ParadiseDetailsLayout"
import { ParadisePager } from "src/components/Paradise/ParadisePager"
import { ParadiseTable } from "src/components/Paradise/ParadiseTable"
import {
  TruncatedEntityId,
  TruncatedHomeId,
  TruncatedUserId,
} from "src/components/Paradise/ParadiseTruncatedIds"
import { ParadiseCreateTokensDialog } from "src/components/Paradise/ParadiseUsers/ParadiseUserDetails/ParadiseUserHomeTokens/ParadiseCreateTokensDialog"
import { ParadiseTransferTokensDialog } from "src/components/Paradise/ParadiseUsers/ParadiseUserDetails/ParadiseUserHomeTokens/ParadiseTransferTokensDialog"
import { ParadiseUserDeleteHomeTokenDialog } from "src/components/Paradise/ParadiseUsers/ParadiseUserDetails/ParadiseUserHomeTokens/ParadiseUserDeleteHomeTokenDialog"
import { ParadiseUserHomeTokensFilters } from "src/components/Paradise/ParadiseUsers/ParadiseUserDetails/ParadiseUserHomeTokens/ParadiseUserHomeTokensFilters"
import { ParadiseUserUpdateHomeTokenDialog } from "src/components/Paradise/ParadiseUsers/ParadiseUserDetails/ParadiseUserHomeTokens/ParadiseUserUpdateHomeTokenDialog"
import { RadioWrapper } from "src/components/Paradise/sharedStyles"
import { TFetchCurrentSubscriptionResponse } from "src/data/billing/types/billingTypes"
import { useFetchUserHomeTokens } from "src/data/homes/queries/homeTokenQueries"
import { THomeToken } from "src/data/homes/types/homeTokenTypes"
import { Routes } from "src/router/routes"
import { useSearchParams } from "src/router/useSearchParams"
import { MButton } from "src/ui/Button/MButton"
import { MoreButton } from "src/ui/GridTable/MoreButton"
import { ColumnPopoverWrapper } from "src/ui/GridTable/useTableColumns/tableColumnStyles"
import { useSortParam } from "src/ui/GridTable/useTableColumns/useSortParam"
import {
  TableColumn,
  useTableColumns,
} from "src/ui/GridTable/useTableColumns/useTableColumns"
import FilterIcon from "src/ui/icons/calibrating.svg"
import { formatPrice } from "src/utils/formatPrice"
import { formatUtcDate } from "src/utils/l10n"
const LIMIT = 15

export function ParadiseUserHomeTokens({
  userId,
  subscription,
}: {
  userId: string
  subscription?: TFetchCurrentSubscriptionResponse | null
}) {
  const [offset, setOffset] = useState(0)
  const [openDialog, setOpenDialog] = useState<
    "create" | "transfer" | "delete" | "edit" | null
  >(null)

  const [selectedHomeToken, setSelectedHomeToken] = useState<THomeToken | null>(
    null
  )
  const [checkedHomeTokens, setCheckedHomeTokens] = useState<THomeToken[]>([])

  const { sortValue, setSortValue } = useSortParam({
    id: "expires_at",
    order: "desc",
  })
  const { searchParams: filter, setSearchParams: setFilter } = useSearchParams({
    keys: [
      {
        key: "home_tokens_activated",
        type: "boolean",
        acceptFalsy: true,
      },
    ],
  })

  const fetchUserHomeTokens = useFetchUserHomeTokens({
    userId,
    filter: {
      limit: LIMIT,
      offset,
      sort: sortValue?.id as "expires_at" | "created_at" | undefined,
      order: sortValue?.order,
      activated: filter.home_tokens_activated ?? undefined,
    },
    options: {
      keepPreviousData: true,
      retry: false,
    },
  })

  const fetchUnactivatedHomeTokens = useFetchUserHomeTokens({
    userId,
    filter: {
      limit: 1,
      offset: 0,
      activated: false,
    },
    options: {
      keepPreviousData: true,
      retry: false,
      enabled: filter.home_tokens_activated === null,
    },
  })

  const tokens = fetchUserHomeTokens.data?.home_tokens ?? []
  // Activated tokens can't be changed
  const checkableTokens = tokens.filter((token) => !token.activated_at)
  const checkedAllHomeTokens =
    checkableTokens.length > 0 &&
    checkableTokens.length === checkedHomeTokens.length

  function reset() {
    setCheckedHomeTokens([])
    setSelectedHomeToken(null)
    setOpenDialog(null)
  }

  function toggleHomeTokenSelected(token: THomeToken | null) {
    if (!token) return
    setCheckedHomeTokens((prev) =>
      prev.includes(token)
        ? prev.filter((t) => t.id !== token.id)
        : [...prev, token]
    )
  }

  const hasTransferredTokens = fetchUserHomeTokens.data?.home_tokens.some(
    (token) => !!token.original_token_owner_id
  )

  const columns: TableColumn<THomeToken>[] = [
    {
      value: "checked",
      label: "",
      renderLabel: () => (
        <Checkbox
          checked={checkedAllHomeTokens}
          onCheck={() => {
            setCheckedHomeTokens(checkedAllHomeTokens ? [] : checkableTokens)
          }}
        />
      ),
      columnWidth: "min-content",
      renderCell: (token) => (
        <div>
          <Checkbox
            disabled={!!token.activated_at}
            checked={!!checkedHomeTokens.find((t) => t.id === token.id)}
            onCheck={() => toggleHomeTokenSelected(token)}
          />
        </div>
      ),
    },
    {
      value: "id",
      label: "Id",
      columnWidth: "min-content",
      renderCell: (token) => <TruncatedEntityId entityId={token.id} />,
    },
    {
      value: "home_id",
      label: "Home id",
      columnWidth: "min-content",
      renderCell: (token) => {
        return (
          <TruncatedHomeId
            homeId={token.home_id}
            to={
              token.activated_at !== undefined
                ? Routes.ParadiseHome.location(token.home_id ?? "")
                : undefined
            }
          />
        )
      },
    },
    {
      value: "created_at",
      label: "Created at",
      enableSort: true,
      sortButtonSpacing: "compact",
      columnWidth: "min-content",
      renderCell: (token) => (
        <Text variant="bodyS">{formatUtcDate({ date: token.created_at })}</Text>
      ),
    },
    {
      value: "age_days",
      label: "Age (days)",
      columnWidth: "min-content",
      enableSort: true,
      renderCell: (token) => (
        <Text variant="bodyS">
          {differenceInDays(new Date(), new Date(token.created_at))}
        </Text>
      ),
    },
    {
      value: "first_renewal_after_activation",
      label: "First renewal after activation",
      columnWidth: "min-content",
      renderCell: (token) => (
        <Text variant="bodyS">
          {token.first_renewal_after_activation
            ? formatUtcDate({
                date: token.first_renewal_after_activation,
              })
            : "-"}
        </Text>
      ),
    },
    {
      value: "activated_at",
      label: "Activated at",
      columnWidth: "min-content",
      renderCell: (token) => (
        <Text variant="bodyS">
          {token.activated_at
            ? formatUtcDate({ date: token.activated_at })
            : "-"}
        </Text>
      ),
      popoverContent: (
        <ColumnPopoverWrapper>
          <RadioWrapper>
            <RadioButton
              name="activated"
              value="false"
              label="Unactivated tokens only"
              selected={filter.home_tokens_activated === false}
              onSelected={() => setFilter("home_tokens_activated", false)}
            />
            <RadioButton
              name="activated"
              value="true"
              label="Activated tokens only"
              selected={filter.home_tokens_activated === true}
              onSelected={() => setFilter("home_tokens_activated", true)}
            />
          </RadioWrapper>
        </ColumnPopoverWrapper>
      ),
      popoverIcon: FilterIcon,
    },
    {
      value: "original_token_owner_id",
      label: "Transferred from",
      columnWidth: "min-content",
      hidden: !hasTransferredTokens,
      renderCell: (token) => (
        <Text variant="bodyS">
          {token.original_token_owner_id ? (
            <TruncatedUserId
              userId={token.original_token_owner_id}
              to={Routes.ParadiseUser.location(token.original_token_owner_id)}
            />
          ) : (
            "-"
          )}
        </Text>
      ),
    },
    {
      value: "expires_at",
      label: "Expires at",
      enableSort: true,
      columnWidth: "min-content",
      renderCell: (token) => (
        <Text variant="bodyS">{formatUtcDate({ date: token.expires_at })}</Text>
      ),
    },
    {
      value: "plan_type",
      label: "Plan type",
      columnWidth: "min-content",
      renderCell: (token) => <Text variant="bodyS">{token.plan_type}</Text>,
    },
    {
      value: "amount_paid",
      label: "Amount paid",
      columnWidth: "min-content",
      renderCell: (token) => (
        <Text variant="bodyS">
          {formatPrice(token.amount_paid / 100, token.currency)}
        </Text>
      ),
    },
    {
      value: "purchased_from",
      label: "Purchased from",
      columnWidth: "min-content",
      renderCell: (token) => (
        <Text variant="bodyS">{token.purchased_from}</Text>
      ),
    },
    {
      value: "updated_at",
      label: "Updated at",
      columnWidth: "min-content",
      renderCell: (token) => (
        <Text variant="bodyS">{formatUtcDate({ date: token.updated_at })}</Text>
      ),
    },
    {
      value: "misc",
      label: "",
      columnWidth: "1fr",
      renderCell: (token) => (
        <div>
          <MoreButtonWrapper>
            <MoreButton
              deleteLabel="Delete token"
              editLabel="Edit expiration date"
              onEdit={() => {
                setSelectedHomeToken(token)
                setOpenDialog("edit")
              }}
              onDelete={() => {
                setSelectedHomeToken(token)
                setOpenDialog("delete")
              }}
              menuItems={[
                {
                  key: "transfer",
                  contents: "Transfer token",
                  onClick: () => {
                    setCheckedHomeTokens([token])
                    setOpenDialog("transfer")
                  },
                },
              ]}
              disabled={!!token.activated_at || !token.id}
            />
          </MoreButtonWrapper>
        </div>
      ),
    },
  ]

  const {
    headerElements,
    rows: newrows,
    templateColumns,
  } = useTableColumns({
    columns,
    data: fetchUserHomeTokens.data?.home_tokens ?? [],
    sort: sortValue,
    onSortChange: (sort) => {
      if (sort.id === "age_days") {
        setSortValue({
          id: "created_at",
          order: sort.order,
        })
      } else {
        setSortValue(sort)
      }
      setOffset(0)
    },
  })

  const isContractBillingModel =
    !!subscription && subscription.billing_model !== "plan_unit_per_home"

  return (
    <div>
      {isContractBillingModel && (
        <Banner type="info" style={{ marginBottom: spacing.L }} fullWidth>
          The user is on a contract billing model. This means that home tokens
          doesn't have any affect on the subscription.
        </Banner>
      )}

      <ParadiseDetailsSection
        title="Home tokens"
        totalCount={
          <div style={{ display: "flex", gap: 8 }}>
            <Badge color="info" size="x-small">
              Total:{" "}
              <Text variant="subtitleS" as="span">
                {fetchUserHomeTokens.data?.paging.total_count}
              </Text>
            </Badge>
            {filter.home_tokens_activated === null && (
              <Badge color="info" size="x-small">
                Unactivated:{" "}
                <Text variant="subtitleS" as="span">
                  {fetchUnactivatedHomeTokens.data?.paging.total_count}
                </Text>
              </Badge>
            )}
          </div>
        }
        actions={
          <ButtonsWrapper>
            <MButton
              variant="subtle"
              size="small"
              disabled={
                checkedHomeTokens.length === 0 || isContractBillingModel
              }
              onClick={() => {
                setOpenDialog("transfer")
              }}
            >
              {checkedHomeTokens.length > 0
                ? `Transfer ${checkedHomeTokens.length} token${
                    checkedHomeTokens.length === 1 ? "" : "s"
                  }`
                : "Transfer tokens"}
            </MButton>
            <MButton
              variant="subtle"
              size="small"
              onClick={() => {
                setOpenDialog("create")
              }}
              disabled={isContractBillingModel}
            >
              Create tokens
            </MButton>
          </ButtonsWrapper>
        }
      />

      <ParadiseUserHomeTokensFilters filter={filter} setFilter={setFilter} />

      <ParadiseTable
        header={headerElements}
        rows={newrows ?? []}
        isLoading={fetchUserHomeTokens.isLoading}
        onRowClick={(rowIndex) => {
          const token = fetchUserHomeTokens.data?.home_tokens[rowIndex]
          if (token && token.activated_at === undefined) {
            toggleHomeTokenSelected(token)
          }
        }}
        templateColumns={templateColumns}
        error={{
          hasError: fetchUserHomeTokens.isError,
          title: fetchUserHomeTokens.error?.message,
        }}
      />

      <ParadisePager
        limit={LIMIT}
        offset={offset}
        setOffset={setOffset}
        totalCount={fetchUserHomeTokens.data?.paging.total_count}
      />

      <ParadiseCreateTokensDialog
        open={openDialog === "create"}
        userId={userId}
        onClose={() => setOpenDialog(null)}
      />

      <ParadiseTransferTokensDialog
        open={openDialog === "transfer"}
        onClose={() => reset()}
        userId={userId}
        tokens={checkedHomeTokens}
      />
      {selectedHomeToken && (
        <ParadiseUserDeleteHomeTokenDialog
          userId={userId}
          open={openDialog === "delete"}
          onClose={() => reset()}
          token={selectedHomeToken}
        />
      )}
      {selectedHomeToken && (
        <ParadiseUserUpdateHomeTokenDialog
          open={openDialog === "edit"}
          token={selectedHomeToken}
          onClose={() => reset()}
        />
      )}
    </div>
  )
}

const MoreButtonWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
`
const ButtonsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;
`
